import React, { useState, useEffect } from 'react';
import { Clock, Users, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useApp } from '../context/AppContext';
import { sessionService } from '../services/sessionService';

interface ApprovalWaitingScreenProps {
  sessionId: string;
  onApproved: () => void;
  onRejected: () => void;
}

const ApprovalWaitingScreen: React.FC<ApprovalWaitingScreenProps> = ({ 
  sessionId, 
  onApproved, 
  onRejected 
}) => {
  const { state } = useApp();
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    // Check approval status periodically
    const checkApprovalStatus = async () => {
      setIsChecking(true);
      try {
        // Check if user is now in the session
        const session = await sessionService.getSession(sessionId);
        if (session && session.currentUsers.includes(state.currentUser.id)) {
          setStatus('approved');
          setTimeout(() => {
            onApproved();
          }, 1000);
        }
      } catch (error) {
        console.error('Error checking approval status:', error);
      } finally {
        setIsChecking(false);
      }
    };

    // Check immediately
    checkApprovalStatus();

    // Check every 2 seconds
    const interval = setInterval(checkApprovalStatus, 2000);

    return () => clearInterval(interval);
  }, [sessionId, state.currentUser.id, onApproved]);

  const handleCancelRequest = async () => {
    try {
      // Remove the pending approval request
      const approvalRef = doc(db, 'pending_approvals', `${sessionId}_${state.currentUser.id}`);
      await deleteDoc(approvalRef);
      onRejected();
    } catch (error) {
      console.error('Failed to cancel approval request:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-dark-900 flex items-center justify-center z-50 p-4">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-accent-500/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-accent-400/15 rounded-full blur-2xl animate-bounce"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 bg-dark-800/95 backdrop-blur-sm rounded-2xl w-full max-w-md p-8 shadow-2xl border border-dark-700 text-center">
        {/* Status Icon */}
        <div className="mb-6">
          {status === 'pending' && (
            <div className="w-20 h-20 bg-accent-500/20 rounded-full flex items-center justify-center mx-auto animate-pulse">
              <Clock className="h-10 w-10 text-accent-500" />
            </div>
          )}
          {status === 'approved' && (
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto animate-scale-in">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
          )}
          {status === 'rejected' && (
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="h-10 w-10 text-red-500" />
            </div>
          )}
        </div>

        {/* Status Message */}
        <div className="mb-6">
          {status === 'pending' && (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Waiting for Approval</h2>
              <p className="text-dark-400">
                Your request to join the session has been sent to the session creator.
              </p>
              <p className="text-sm text-dark-500 mt-2">
                Please wait while they review your request...
              </p>
            </>
          )}
          {status === 'approved' && (
            <>
              <h2 className="text-2xl font-bold text-green-500 mb-2">Approved!</h2>
              <p className="text-dark-400">
                Your request has been approved. Joining the session...
              </p>
            </>
          )}
          {status === 'rejected' && (
            <>
              <h2 className="text-2xl font-bold text-red-500 mb-2">Request Rejected</h2>
              <p className="text-dark-400">
                Your request to join the session was not approved.
              </p>
            </>
          )}
        </div>

        {/* Session Info */}
        <div className="bg-dark-700/50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Users className="h-5 w-5 text-accent-500" />
            <span className="text-white font-medium">Session ID: {sessionId}</span>
          </div>
          <p className="text-sm text-dark-400">
            You will be automatically added to the session once approved.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {status === 'pending' && (
            <>
              <div className="flex items-center justify-center space-x-2 text-sm text-dark-400">
                <RefreshCw className={`h-4 w-4 ${isChecking ? 'animate-spin' : ''}`} />
                <span>Checking for updates...</span>
              </div>
              <button
                onClick={handleCancelRequest}
                className="w-full bg-dark-600 hover:bg-dark-700 text-white rounded-lg py-3 font-medium transition-all duration-200"
              >
                Cancel Request
              </button>
            </>
          )}
          {status === 'rejected' && (
            <button
              onClick={onRejected}
              className="w-full bg-accent-600 hover:bg-accent-700 text-white rounded-lg py-3 font-medium transition-all duration-200"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalWaitingScreen;
